import {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { SharedBrief, sharedBriefLastStep } from "./SharedBrief";
import { ExampleLabel, Icon, useInView, useReducedMotion } from "./shared";

const CustomerApp = lazy(() =>
  import("./demos").then((module) => ({ default: module.CustomerApp })),
);
const Automation = lazy(() =>
  import("./demos").then((module) => ({ default: module.Automation })),
);
const plots = [
  {
    name: "Shared context",
    icon: "page",
    note: "A little common ground.",
    description: "Maya and Claude get Juniper Café’s first delivery right.",
  },
  {
    name: "Apps",
    icon: "grid",
    note: "Something useful takes root.",
    description:
      "Stripe payments + plant-care notes. The nursery’s own customer app.",
  },
  {
    name: "Autopilot",
    icon: "clock",
    note: "Good work keeps growing.",
    description:
      "Catch a failed payment before Friday’s plants leave the nursery.",
  },
] as const;

export function GardenExplorer() {
  const [replayId, setReplayId] = useState(0);
  const [selected, setSelected] = useState(0);
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(true);
  const reduced = useReducedMotion();
  const { ref, visible } = useInView<HTMLDivElement>();
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);
  useEffect(() => {
    if (reduced) {
      setPlaying(false);
      setStep(sharedBriefLastStep);
    }
  }, [reduced]);
  useEffect(() => {
    if (!playing || !visible || reduced) return;
    const timer = window.setTimeout(() => {
      if (step < sharedBriefLastStep) setStep((value) => value + 1);
      else setPlaying(false);
    }, 2400);
    return () => clearTimeout(timer);
  }, [playing, visible, reduced, step]);
  const choose = (index: number) => {
    setSelected(index);
    setPlaying(false);
  };
  const keyboard = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next = index;
    if (event.key === "ArrowRight") next = (index + 1) % plots.length;
    else if (event.key === "ArrowLeft")
      next = (index + plots.length - 1) % plots.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = plots.length - 1;
    else return;
    event.preventDefault();
    choose(next);
    tabs.current[next]?.focus();
  };
  const replay = () => {
    setReplayId((value) => value + 1);
    setSelected(0);
    setStep(reduced ? sharedBriefLastStep : 0);
    setPlaying(!reduced);
  };
  return (
    <div
      className="g-explorer"
      id="garden"
      ref={ref}
      data-visible={visible}
      data-playing={playing}
    >
      <div className="g-explorer-heading">
        <span className="g-eyebrow">ONE NURSERY. A WHOLE TEAM’S KNOW-HOW.</span>
        <span className="g-handwritten">
          Pick a plot. See what grows. <Icon name="curvedArrow" />
        </span>
      </div>
      <p className="g-story-intro">
        Meet <strong>Fern &amp; Field</strong>, our fictional online plant
        nursery. Juniper Café opens on Friday. Follow the team from a shared
        delivery brief to happy plants on the doorstep.
      </p>
      <div
        className="g-plot-tabs"
        role="tablist"
        aria-label="Explore the garden"
      >
        {plots.map((plot, index) => (
          <button
            key={plot.name}
            ref={(node) => {
              tabs.current[index] = node;
            }}
            id={`garden-tab-${index}`}
            role="tab"
            aria-selected={selected === index}
            aria-controls="garden-preview"
            tabIndex={selected === index ? 0 : -1}
            onKeyDown={(event) => keyboard(event, index)}
            onClick={() => choose(index)}
          >
            <Icon name={plot.icon} />
            <span>{plot.name}</span>
            <span className="g-tab-number">0{index + 1}</span>
          </button>
        ))}
      </div>
      <div className="g-garden-stage" data-plot={selected}>
        <picture className="g-garden-art">
          <source
            media="(max-width: 600px)"
            srcSet="/garden/allotment-small.webp"
          />
          <img
            src="/garden/allotment.webp"
            width="1536"
            height="1024"
            alt=""
            decoding="async"
          />
        </picture>
        <svg
          className="g-garden-paths"
          viewBox="0 0 1100 600"
          aria-hidden="true"
        >
          <path
            className={selected === 0 ? "is-selected" : ""}
            d="M125 415Q240 535 545 470"
          />
          <path
            className={selected === 1 ? "is-selected" : ""}
            d="M545 470Q840 520 960 380"
          />
          <path
            className={selected === 2 ? "is-selected" : ""}
            d="M960 380Q900 510 570 540"
          />
        </svg>
        <button
          className="g-plot-marker g-marker-context"
          aria-label="Explore shared context plot"
          aria-pressed={selected === 0}
          onClick={() => choose(0)}
        >
          <Icon name="page" />
          <span>Context</span>
          <Icon name="plus" />
        </button>
        <button
          className="g-plot-marker g-marker-apps"
          aria-label="Explore apps plot"
          aria-pressed={selected === 1}
          onClick={() => choose(1)}
        >
          <Icon name="grid" />
          <span>Apps</span>
          <Icon name="plus" />
        </button>
        <button
          className="g-plot-marker g-marker-auto"
          aria-label="Explore autopilot plot"
          aria-pressed={selected === 2}
          onClick={() => choose(2)}
        >
          <Icon name="clock" />
          <span>Autopilot</span>
          <Icon name="plus" />
        </button>
        <div
          className="g-preview"
          role="tabpanel"
          id="garden-preview"
          aria-labelledby={`garden-tab-${selected}`}
          tabIndex={0}
        >
          <Suspense
            fallback={
              <div className="g-demo-loading" role="status">
                Growing your example…
              </div>
            }
          >
            {selected === 0 ? (
              <SharedBrief key={replayId} step={step} />
            ) : selected === 1 ? (
              <CustomerApp compact />
            ) : (
              <Automation compact />
            )}
          </Suspense>
        </div>
        <span className="g-garden-annotation" aria-hidden="true">
          {plots[selected].note}
        </span>
      </div>
      <div className="g-explorer-footer">
        <ExampleLabel />
        <p>{plots[selected].description}</p>
        <div className="g-playback">
          <button
            onClick={() => {
              setSelected(0);
              if (step === sharedBriefLastStep && !playing) replay();
              else setPlaying((value) => !value);
            }}
            aria-label={playing ? "Pause introduction" : "Play introduction"}
            disabled={reduced}
          >
            <Icon name={playing ? "pause" : "play"} />
          </button>
          <button onClick={replay} aria-label="Replay introduction">
            <Icon name="replay" />
          </button>
        </div>
      </div>
    </div>
  );
}
